/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { TypedEventEmitter } from "@fluid-internal/client-utils";
import { AttachState, } from "@fluidframework/container-definitions";
/**
 * Creates an {@link IFluidContainer} from the provided `container` and `rootDataObject`.
 *
 * @internal
 */
export function createFluidContainer(props) {
    return new FluidContainer(props.container, props.rootDataObject);
}
/**
 * Check that the provided `container` is an internal {@link IFluidContainerInternal}.
 *
 * @internal
 */
export function isInternalFluidContainer(container) {
    // IFluidContainer is sealed; so we never expect an `IFluidContainer` not to be
    // `IFluidContainerInternal` implemented by `FluidContainer`. To be caution,
    // we use `instanceof` to confirm.
    return container instanceof FluidContainer;
}
/**
 * {@link IFluidContainer} implementation.
 *
 * @typeparam TContainerSchema - Used to determine the type of 'initialObjects'.
 * @remarks
 *
 * Note: this implementation is not complete. Consumers who rely on {@link IFluidContainer.attach}
 * will need to utilize or provide a service-specific implementation of this type that implements that method.
 */
class FluidContainer extends TypedEventEmitter {
    constructor(container, rootDataObject) {
        super();
        this.container = container;
        this.rootDataObject = rootDataObject;
        this.connectedHandler = () => this.emit("connected");
        this.disconnectedHandler = () => this.emit("disconnected");
        this.disposedHandler = (error) => this.emit("disposed", error);
        this.savedHandler = () => this.emit("saved");
        this.dirtyHandler = () => this.emit("dirty");
        container.on("connected", this.connectedHandler);
        container.on("closed", this.disposedHandler);
        container.on("disconnected", this.disconnectedHandler);
        container.on("saved", this.savedHandler);
        container.on("dirty", this.dirtyHandler);
    }
    get isDirty() {
        return this.container.isDirty;
    }
    get attachState() {
        return this.container.attachState;
    }
    get disposed() {
        return this.container.closed;
    }
    get connectionState() {
        return this.container.connectionState;
    }
    get initialObjects() {
        return this.rootDataObject.initialObjects;
    }
    /**
     * Incomplete base implementation of {@link IFluidContainer.attach}.
     *
     * @remarks
     *
     * Note: this implementation will unconditionally throw.
     * Consumers who rely on this will need to utilize or provide a service specific implementation of this base type
     * that provides an implementation of this method.
     *
     * The reason is because externally we are presenting a separation between the service and the `FluidContainer`,
     * but internally this separation is not there.
     */
    async attach(props) {
        if (this.container.attachState !== AttachState.Detached) {
            throw new Error("Cannot attach container. Container is not in detached state.");
        }
        throw new Error("Cannot attach container. Attach method not provided.");
    }
    async connect() {
        this.container.connect?.();
    }
    async disconnect() {
        this.container.disconnect?.();
    }
    async create(objectClass) {
        return this.rootDataObject.create(objectClass);
    }
    dispose() {
        this.container.close();
        this.container.off("connected", this.connectedHandler);
        this.container.off("closed", this.disposedHandler);
        this.container.off("disconnected", this.disconnectedHandler);
        this.container.off("saved", this.savedHandler);
        this.container.off("dirty", this.dirtyHandler);
    }
}
//# sourceMappingURL=fluidContainer.js.map